import React, { useCallback, useMemo, useRef, useState } from 'react';
import ReactSelect, { components } from 'react-select';
import ReactSelectAsync from 'react-select/async';
import ReactSelectAsyncCreatable from 'react-select/async-creatable';
import ReactSelectCreatable from 'react-select/creatable';
import { useTheme } from 'styled-components';
import { Button } from '../button';
import { StyledButton } from '../chip-input/styled';
import { FormHelperText } from '../form-helper-text';
import { FormLabel } from '../form-label';
import { Icon } from '../icon';
import { LoadingIndicator } from '../loading-indicator';
import { MenuListItem } from '../menu';
import { Popover } from '../popover';
import { DropdownIndicatorIcon, SelectedIcon } from './icons';
import { IconContainer, PopoverContainer, StyledSelectWrapper, styleReactSelect } from './styled';
import VirtualizedList from './virtualized-list';
import { CreateOptionWrapper } from '../menu/styled';
import { differenceWith, eqBy, isEmpty, prop } from 'ramda';
import { Chip } from '../chip';
const withPrefixIconValueContainer = (prefixIcon) => ({ children, ...props }) => {
    return (React.createElement(components.ValueContainer, { ...props },
        prefixIcon && React.createElement(IconContainer, null, prefixIcon),
        children));
};
const Menu = ({ children, maxMenuHeight, ...props }) => {
    return (React.createElement(components.Menu, { maxMenuHeight: maxMenuHeight, ...props }, children));
};
const Option = ({ children, isSelected, isDisabled, isFocused, innerProps, innerRef, selectProps, data }) => {
    const { required, __isNew__: isNew } = data;
    const onClick = useCallback((e) => !required && !isSelected && innerProps.onClick(e), [innerProps.onClick, isSelected, required]);
    if (isNew) {
        return (React.createElement(CreateOptionWrapper, { ...innerProps, onClick: onClick },
            React.createElement(Icon, { name: "plus" }),
            "Add New \"",
            data.value,
            "\""));
    }
    return (React.createElement(MenuListItem, { size: selectProps.size, ref: innerRef, checked: isSelected, disabled: isDisabled, focused: isFocused, ...innerProps, onClick: onClick },
        children,
        isSelected && React.createElement(SelectedIcon, null)));
};
const MultiValue = ({ clearValue, data, selectProps, removeProps, ...rest }) => {
    const disabled = selectProps.isDisabled;
    const required = data.required;
    const onRemove = useCallback(removeProps.onClick, [removeProps.onClick]);
    return (React.createElement(Chip, { id: data.value, type: "chipInput", variant: "default", title: data.label, disabled: disabled || required, onRemove: onRemove, size: "small", ...rest }));
};
const genComponents = ({ prefixIcon, isMulti, withClearConfirmation, hideClearButton, virtualized, hasOnlyRequired }) => ({
    ClearIndicator: hideClearButton || hasOnlyRequired
        ? null
        : ({ clearValue }) => {
            const [isOpen, setOpen] = useState(false);
            const handleClose = () => setOpen(false);
            const handleOpen = () => setOpen(true);
            const [anchorEl, setAnchorEl] = useState(null);
            const popoverRef = useRef(null);
            return (React.createElement(React.Fragment, null,
                React.createElement(StyledButton, { ref: setAnchorEl, onClick: isMulti && withClearConfirmation ? handleOpen : clearValue, title: "Remove", variant: "transparent", EndIcon: React.createElement(Icon, { name: "close_outline" }) }),
                React.createElement(Popover, { ref: popoverRef, isOpen: isOpen, anchorEl: anchorEl, positions: ['bottom', 'top'], onClickOutside: handleClose },
                    React.createElement(PopoverContainer, null,
                        React.createElement(Button, { size: "small", text: "Confirm", onClick: clearValue })))));
        },
    IndicatorSeparator: null,
    DropdownIndicator: DropdownIndicatorIcon,
    ValueContainer: withPrefixIconValueContainer(prefixIcon),
    MultiValue,
    Menu,
    ...(virtualized ? { MenuList: VirtualizedList } : {}),
    Option,
    Control: components.Control,
    LoadingIndicator: LoadingIndicator
});
const BaseSelect = (props) => {
    var _a, _b;
    const { Component, id, label, labelTooltip, ariaLabel, size = 'medium', placeholder = null, prefixIcon, groupPosition, disabled, styles = {}, hint, hintType, fixedPadding, required, isMulti, components: propagatedComponents, options: propagatedOptions, value, onChange, serverSideSearch, ...rest } = props;
    const theme = useTheme();
    const selectTheme = useMemo(() => ({
        borderRadius: 6,
        colors: theme.colors
    }), [theme]);
    const ref = useRef(null);
    const inputId = (_b = (_a = ref.current) === null || _a === void 0 ? void 0 : _a.select.inputRef) === null || _b === void 0 ? void 0 : _b.id;
    const requiredOptions = useMemo(() => { var _a; return (_a = propagatedOptions === null || propagatedOptions === void 0 ? void 0 : propagatedOptions.filter((option) => option === null || option === void 0 ? void 0 : option.required)) !== null && _a !== void 0 ? _a : []; }, [propagatedOptions]);
    const hasOnlyRequired = isMulti
        ? isEmpty(differenceWith(eqBy(prop('value')), value !== null && value !== void 0 ? value : [], requiredOptions))
        : false;
    const components = useMemo(() => genComponents({
        prefixIcon,
        isMulti: rest.isMulti,
        withClearConfirmation: rest.withClearConfirmation,
        theme,
        hideClearButton: rest.hideClearButton,
        virtualized: props.virtualized,
        hasOnlyRequired
    }), [
        prefixIcon,
        rest.isMulti,
        rest.withClearConfirmation,
        rest.hideClearButton,
        theme,
        props.virtualized,
        hasOnlyRequired
    ]);
    const styledObject = {
        ...styleReactSelect(theme, size, hintType, prefixIcon, groupPosition),
        ...styles
    };
    const selectedValue = useMemo(() => {
        if (!isMulti) {
            return value;
        }
        const valueWithoutRequired = differenceWith(eqBy(prop('value')), value !== null && value !== void 0 ? value : [], requiredOptions);
        return [...requiredOptions, ...valueWithoutRequired];
    }, [propagatedOptions, value]);
    return (React.createElement(StyledSelectWrapper, null,
        label && (React.createElement(FormLabel, { htmlFor: inputId, labelTooltip: labelTooltip, required: required }, label)),
        React.createElement(Component, { id: id, ref: ref, "aria-label": ariaLabel || label, required: required, isOpen: true, theme: selectTheme, captureMenuScroll: false, hideSelectedOptions: false, blurInputOnSelect: true, maxMenuHeight: 150, styles: styledObject, size: size, placeholder: placeholder, components: { ...components, ...propagatedComponents }, isMulti: isMulti, value: selectedValue, options: propagatedOptions, onChange: onChange, filterOption: serverSideSearch ? null : undefined, ...rest, isDisabled: disabled }),
        React.createElement(FormHelperText, { fixedPadding: fixedPadding, msgType: hintType }, hint)));
};
export const Select = (props) => {
    return React.createElement(BaseSelect, { Component: ReactSelect, ...props });
};
export const SelectCreatable = (props) => {
    return React.createElement(BaseSelect, { Component: ReactSelectCreatable, ...props });
};
export const SelectAsync = (props) => {
    return React.createElement(BaseSelect, { Component: ReactSelectAsync, ...props });
};
export const SelectAsyncCreatable = (props) => {
    return React.createElement(BaseSelect, { Component: ReactSelectAsyncCreatable, ...props });
};
